import { useEffect, useState } from "react";
import { fetchMySessions, renameSession, deleteOwnSession } from "@/lib/api";
import { ArrowLeft, Loader2, FileText, Clock, CheckCircle2, PenLine, Pencil, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

// "My Work" library. Lists a signed-in learner's sessions. Finished sessions open
// the Final Review; in-progress sessions resume where they left off. Each item can
// be renamed inline.
export default function MyWork({ onBack, onResume, onOpenReview }) {
  const [sessions, setSessions] = useState(null);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState("");
  const removeSession = async (id) => {
    setSavingId(id);
    try { await deleteOwnSession(id); setSessions((prev) => prev.filter((x) => x.id !== id)); toast.success("Piece deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete this piece"); }
    finally { setSavingId(""); setConfirmDelete(""); }
  };
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    fetchMySessions()
      .then(setSessions)
      .catch((e) => setError(e.response?.data?.detail || "Could not load your work."));
  }, []);

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return ""; }
  };

  const displayTitle = (s) => (s.title || "").trim() || (s.assignment || "").trim() || "Untitled paragraph";

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditValue((s.title || "").trim());
  };
  const cancelEdit = () => { setEditingId(null); setEditValue(""); };
  const saveEdit = async (id) => {
    const title = editValue.trim().slice(0, 120);
    setSavingId(id);
    try {
      await renameSession(id, title);
      setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));
      cancelEdit();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not rename.");
    }
    setSavingId(null);
  };

  return (
    <div className="flex-1 flex flex-col py-6 w-full max-w-3xl mx-auto" data-testid="my-work">
      <button
        onClick={onBack}
        data-testid="my-work-back"
        className="inline-flex items-center gap-2 text-stone-500 hover:text-[#8C3A2A] transition-colors font-mono-panel text-[11px] uppercase tracking-[0.16em] mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <h1 className="font-serif-display text-3xl text-stone-900 mb-1">My Work</h1>
      <p className="text-[14px] text-stone-500 mb-8">Your saved paragraphs and coaching sessions.</p>

      {error && <p className="text-[14px] text-red-600" data-testid="my-work-error">{error}</p>}

      {sessions === null && !error && (
        <div className="flex items-center gap-2 text-stone-400 text-[14px]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your work…
        </div>
      )}

      {sessions && sessions.length === 0 && (
        <div data-testid="my-work-empty" className="border border-dashed border-stone-300 rounded-sm p-10 text-center">
          <FileText className="h-8 w-8 text-stone-300 mx-auto mb-3" />
          <p className="text-[15px] text-stone-600 mb-1">No saved work yet</p>
          <p className="text-[13px] text-stone-400">Anything you write with The Writing Studio while signed in will appear here.</p>
        </div>
      )}

      {sessions && sessions.length > 0 && (
        <ul className="space-y-3" data-testid="my-work-list">
          {sessions.map((s) => {
            const finished = s.status === "finished";
            const isEditing = editingId === s.id;
            return (
              <li
                key={s.id}
                data-testid={`my-work-item-${s.id}`}
                className="border border-stone-200 rounded-sm p-4 hover:border-[#8C3A2A]/40 transition-colors bg-white"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-mono-panel uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm ${finished ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {finished ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                        {finished ? "Finished" : "In progress"}
                      </span>
                      <span className="text-[11px] text-stone-400">{fmt(s.updated_at || s.created_at)}</span>
                    </div>

                    {isEditing ? (
                      <div className="flex items-center gap-2 mb-1">
                        <input
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(s.id); if (e.key === "Escape") cancelEdit(); }}
                          maxLength={120}
                          placeholder="Name this paragraph"
                          data-testid={`my-work-rename-input-${s.id}`}
                          className="flex-1 border border-[#8C3A2A]/40 rounded-sm px-2 py-1 text-[15px] text-stone-900 focus:border-[#8C3A2A] outline-none"
                        />
                        <button
                          onClick={() => saveEdit(s.id)} disabled={savingId === s.id}
                          data-testid={`my-work-rename-save-${s.id}`}
                          className="shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-sm bg-[#8C3A2A] text-white hover:bg-[#6B2C20] transition-colors disabled:opacity-50"
                          aria-label="Save name"
                        >
                          {savingId === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          onClick={cancelEdit}
                          data-testid={`my-work-rename-cancel-${s.id}`}
                          className="shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-sm border border-stone-300 text-stone-500 hover:text-stone-800 transition-colors"
                          aria-label="Cancel"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 mb-1 group">
                        <h3 className="text-[15px] font-medium text-stone-900 truncate">{displayTitle(s)}</h3>
                        <button
                          onClick={() => startEdit(s)}
                          data-testid={`my-work-rename-${s.id}`}
                          className="shrink-0 text-stone-300 hover:text-[#8C3A2A] transition-colors"
                          aria-label="Rename"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}

                    <p className="text-[13.5px] text-stone-600 line-clamp-2">
                      {s.preview || <span className="italic text-stone-400">No writing yet</span>}
                    </p>
                  </div>
                  {confirmDelete === s.id ? (
                    <span data-testid={`my-work-delete-confirm-${s.id}`} className="shrink-0 flex items-center gap-2 text-[12px]">
                      <span className="text-stone-600">Delete this piece for good?</span>
                      <button data-testid={`my-work-delete-yes-${s.id}`} onClick={() => removeSession(s.id)} disabled={savingId === s.id} className="px-2 py-1 rounded-sm bg-[#8C3A2A] text-white hover:bg-[#6B2C20] disabled:opacity-50">Yes, delete</button>
                      <button data-testid={`my-work-delete-no-${s.id}`} onClick={() => setConfirmDelete("")} className="px-2 py-1 rounded-sm border border-stone-300 text-stone-600">Keep</button>
                    </span>
                  ) : (
                  <button
                    data-testid={`my-work-delete-${s.id}`}
                    onClick={() => setConfirmDelete(s.id)}
                    title="Delete this piece"
                    aria-label="Delete this piece"
                    className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-sm text-stone-300 hover:text-[#8C3A2A] transition-colors"
                  ><Trash2 className="h-4 w-4" /></button>
                  )}
                  <button
                    onClick={() => (finished ? onOpenReview(s.id) : onResume(s.id))}
                    data-testid={`my-work-open-${s.id}`}
                    className="shrink-0 inline-flex items-center gap-1.5 text-[12.5px] px-3 py-1.5 rounded-sm bg-[#8C3A2A] text-white hover:bg-[#6B2C20] transition-colors"
                  >
                    {finished ? <><FileText className="h-3.5 w-3.5" /> View</> : <><PenLine className="h-3.5 w-3.5" /> Resume</>}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
